import { Fragment, useState } from "react";

import {
  Check,
  Expand,
  Minimize,
} from "@hl/base-components/lib/assets/icons.generated";
import {
  BACKGROUND_COLOR,
  STATUS_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { maskAddress } from "@hl/communities-app/lib/utils/content";
import { GenArtIFrame } from "@hl/shared-features/lib/features/gen-art/GenArtIFrame";
import { useGenParams } from "@hl/shared-features/lib/features/gen-art/gen-art-utils";
import {
  Group,
  Text,
  Button,
  Modal,
  ActionIcon,
  Card,
  Divider,
  createStyles,
  Center,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { GetManageCollectionQuery } from "~/features/manage-collection/v2.queries.graphql.generated";
import { PropertyBox } from "~/features/tokens/sections/ManageToken/properties/PropertyBox";

import { getDetailPageUrl } from "../../config";

const useStyles = createStyles({
  container: {
    height: `calc(100vh - 30px)`,
    minHeight: 720,
    overflow: "hidden",
  },
  hashInfo: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
  },
  closeBtn: {
    position: "absolute",
    top: 10,
    right: 10,
  },
});

export const CurateOutputProperty = ({
  collection,
  generativeDetails,
  genSeriesHashes,
}: {
  collection: { id: string; onchainId: string };
  generativeDetails?: GetManageCollectionQuery["getManageCollection"]["generativeDetails"];
  genSeriesHashes?: { hash: string; tokenId: number }[] | null;
}) => {
  const { classes } = useStyles();
  const allowedHashes = generativeDetails?.allowedHashes ?? [];
  const [opened, { open, close }] = useDisclosure(false);
  const mediaUrl = generativeDetails?.generativeCodeUri ?? "";
  const { params } = useGenParams({ allowedHashes, isCurated: true });
  const [selectedHash, selectHash] = useState<{
    hash: string;
    tokenId: number | null;
  } | null>(null);
  const closePreview = () => {
    selectHash(null);
  };

  return (
    <>
      <PropertyBox
        label="Curate output"
        value={
          <Group position="apart" w="100%">
            <Text size="sm" fw={500}>
              {allowedHashes.length} outputs selected
            </Text>
            <Button color={BACKGROUND_COLOR.SECONDARY} onClick={open}>
              View
            </Button>
          </Group>
        }
      />
      <Modal
        opened={opened && !selectedHash}
        onClose={close}
        title="Curate output"
        size="lg"
      >
        <Text size="sm">
          These hashes are allowed as inputs to your generative script.
        </Text>
        <Card withBorder mt={16} py={0}>
          {genSeriesHashes?.map((hashData, index) => (
            <Fragment key={index}>
              <Group position="apart" py={8}>
                <Text size="sm">{maskAddress(hashData.hash, 6, 16)}</Text>
                <Group spacing={24}>
                  {hashData.tokenId && (
                    <>
                      <Text
                        color={STATUS_COLOR.AFFIRMATIVE}
                        size="sm"
                        fw={WEIGHT_BOLD}
                      >
                        <Group spacing={4}>
                          <Check />
                          Minted
                        </Group>
                      </Text>
                      <Text
                        component="a"
                        size="sm"
                        fw={WEIGHT_BOLD}
                        href={getDetailPageUrl(
                          collection,
                          hashData.tokenId.toString()
                        )}
                        target="_blank"
                      >
                        View token
                      </Text>
                    </>
                  )}
                  <ActionIcon
                    onClick={() =>
                      selectHash({
                        hash: hashData.hash,
                        tokenId: hashData.tokenId,
                      })
                    }
                  >
                    <Expand width={16} height={16} />
                  </ActionIcon>
                </Group>
              </Group>
              {index !== allowedHashes.length - 1 && <Divider />}
            </Fragment>
          ))}
        </Card>
      </Modal>
      <Modal
        opened={!!selectedHash}
        onClose={closePreview}
        fullScreen
        withCloseButton={false}
        centered
        styles={{
          body: {
            maxHeight: "100%",
          },
        }}
      >
        <div className={classes.container}>
          <GenArtIFrame
            bordered={false}
            mediaUrl={mediaUrl}
            {...params}
            txHash={selectedHash?.hash ?? params.txHash}
            tokenId={selectedHash?.tokenId ?? params.tokenId}
          />
        </div>
        <Center className={classes.hashInfo}>
          <Card h={50} p={8} bg="primaryBackground.0">
            <Text size="xs" fw={500}>
              Hash
            </Text>
            <Text size="xs">{selectedHash?.hash}</Text>
          </Card>
        </Center>
        <ActionIcon className={classes.closeBtn} onClick={closePreview}>
          <Minimize />
        </ActionIcon>
      </Modal>
    </>
  );
};
